import React, { useState } from 'react'
import Icon from 'components/elements/Icon'
import Link from 'components/elements/Link'

import CommentService from 'services/comment.service'
import ToastService from 'services/toast.service'

import { Comment as CommentType } from 'services/apiTypes/comment.types'
import { DeckCardInfoType } from 'types/search'

import Comment from 'components/comments/Comment'
import PhatButton from 'components/formElements/PhatButton'
import ContestSubmissionTabs from './ContestSubmissionTabs'

import styles from './thread.module.scss'


const NEGATIVE_THRESHOLD = -2

// Helper function that recursively goes through a comment tree and updates the "updated" comment
// Exporting here as the <Thread /> will likely need this in every implementation of onCommentUpdate
// We cannot just do it within the component itself since we only want the recursion to happen on the root component
export const updateCommentChain = (root: CommentType, updatedComment: CommentType): CommentType => {
  if (root.id === updatedComment.id) return updatedComment

  return {
    ...root,
    children: root.children.map(child => updateCommentChain(child, updatedComment)),
  }
}

export const removeUserInputs = (comment: CommentType): CommentType => {
  if (!comment.children) return { ...comment, userInput: 0 }

  return {
    ...comment,
    userInput: 0,
    children: comment.children.map(child => removeUserInputs(child)),
  }
}

type Props = {
  comment: CommentType
  onCommentUpdate: (updatedComment: CommentType) => void
  noCollapse?: boolean
  children?: React.ReactNode
  className?: string
  newsPostStyling?: boolean
  level?: number
  archived?: boolean
  locked?: boolean
  rootPageComment?: boolean
  noPaginatorView?: boolean
  contestPage?: boolean
  noChildren?: boolean
}

const Thread = ({
  comment,
  noCollapse,
  onCommentUpdate,
  children,
  className = '',
  level = 0,
  archived = false,
  locked = false,
  newsPostStyling = false,
  rootPageComment = false,
  noPaginatorView = false,
  contestPage = false,
  noChildren = false,
}: Props) => {
  const minimizedByDefault = comment.points < NEGATIVE_THRESHOLD && !rootPageComment
  const [collapsed, setCollapsed] = useState(minimizedByDefault)

  const handleLoadChildren = async () => {
    await CommentService.details(comment.id)
      .then(onCommentUpdate)
      .catch(() => ToastService.create('Unable to fetch child comments'))
  }

  if (collapsed)
    return (
      <button className={styles.uncollapse} onClick={() => setCollapsed(false)}>
        <span className={styles.collapsedLeft}>
          <Icon name="plus" />
          {comment.owner.username}
          {minimizedByDefault && <span className={styles.belowThreshold}>(Comment scored below threshold)</span>}
        </span>{' '}
        <span className={styles.collapsedRight}>(Replies {comment.childrenCount})</span>
      </button>
    )

  return (
    <div className={`${styles.thread} ${rootPageComment ? styles.noMargin : ''} ${className}`}>
      {/* By default the component renders the root comment as a comment, otherwise it can be passed as it's children */}
      {children === undefined ? (
        <Comment
          comment={comment}
          onCommentChange={onCommentUpdate}
          onCollapse={level !== 0 ? () => setCollapsed(true) : undefined}
          archived={archived}
          locked={locked}
          newsPostStyling={newsPostStyling}
        />
      ) : (
        <>{children}</>
      )}

      {!noCollapse && (
        <button className={styles.collapse} onClick={() => setCollapsed(true)}>
          <Icon name="window minimize" />
        </button>
      )}

      {contestPage && <ContestSubmissionTabs threadId={comment.id} showAll={noChildren}/>}

      {/* Recursviely Rendering Children */}
      {!noChildren && comment.children.map((child, key) => (
        <Thread comment={child} key={key} onCommentUpdate={onCommentUpdate} level={level + 1} archived={archived} />
      ))}

      {/* Don't show load more button on threads that are the root for their page, we'll use the paginator for that */}
      {comment.children.length !== comment.childrenCount && level < 6 && !rootPageComment && (
        <button className={styles.loadMore} onClick={handleLoadChildren}>
          Load child comments <Icon name="level down" />
        </button>
      )}

      {comment.children.length !== comment.childrenCount && level >= 6 && (
        <Link className={styles.loadMore} href={`/forum/thread/${comment.id}`}>
          Continue this thread on new page <Icon name="arrow right" />
        </Link>
      )}

      {/* If we have no paginator, insert a psudo paginator to take us to a paged link */}
      {noPaginatorView && level === 0 && comment.childrenCount > comment.children.length && !!comment.deck?.id && (
        <div className={styles.center}>
          <Link href={`/forum/thread/${comment.id}`}>
            <PhatButton color="orange">View all comments</PhatButton>
          </Link>
        </div>
      )}
    </div>
  )
}

export default Thread
